import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * @typedef Props
 * @prop {boolean} [circle]
 * @prop {boolean} [isIndeterminate]
 * @prop {string} [className]
 * @prop {number} [value]
 * @prop {boolean} [error]
 * @prop {boolean} [showLabel]
 * @prop {boolean} [hideLabel]
 * @prop {React.ReactNode} [labelComponent]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifProgressBar = (props) => {
  const {
    circle = false,
    isIndeterminate = false,
    className = '',
    value = 0,
    error = false,
    showLabel = false,
    hideLabel = false,
    labelComponent = null,
    ...other
  } = props;
  const wrapperClasses = classNames({
    'motif-progress-bar-wrapper': true,
    'motif-progress-bar-wrapper-circle': circle,
    [className]: true
  });

  const classes = classNames({
    'motif-progress-bar': true,
    'motif-progress-bar-circle': circle,
    'motif-progress-bar-indeterminate': isIndeterminate
  });

  const renderProgessBar = () => {
    const progressClasses = classNames({
      'motif-progress': true,
      'motif-progress-error': error
    });

    return (
      <div className={progressClasses} style={isIndeterminate ? null : { width: `${value}%` }} />
    );
  };

  const renderProgessBarPie = useMemo(() => {
    const progressClasses = classNames({
      'motif-progress-pie': true,
      'motif-progress-hide-right': value <= 50,
      'motif-progress-error': error
    });

    const DEGREE_MULTIPLIER = 3.6;

    return (
      <React.Fragment>
        <div className={progressClasses}>
          <div
            className="motif-progress-left-side motif-progress-half-circle"
            style={{ transform: `rotate(${(isIndeterminate ? 25 : value) * DEGREE_MULTIPLIER}deg` }}
          />
          <div className="motif-progress-right-side motif-progress-half-circle" />
        </div>
        <div className="motif-progress-shadow" />
      </React.Fragment>
    );
  }, [value, error, isIndeterminate]);

  return (
    <div className={wrapperClasses} data-motif-progress-bar>
      {labelComponent || (
        <span
          aria-live="polite"
          className={classNames({
            'motif-progress-bar-label': true,
            /* 'motif-progress-bar-label-hidden': !showLabel, */
            'motif-progress-bar-label-hidden': hideLabel
          })}
        >
          {isIndeterminate && !circle ? 'Processing...' : `${value}%${circle ? '' : ' Complete'}`}
        </span>
      )}
      <div {...other} className={classes}>
        {circle ? renderProgessBarPie : renderProgessBar()}
      </div>
    </div>
  );
};

MotifProgressBar.propTypes = {
  className: PropTypes.string,
  value: PropTypes.number,
  circle: PropTypes.bool,
  error: PropTypes.bool,
  isIndeterminate: PropTypes.bool,
  showLabel: PropTypes.bool,
  hideLabel: PropTypes.bool,
  labelComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType])
};

export { MotifProgressBar };
